import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MessageCircle,
  Send,
  Search,
  RefreshCw,
  Smartphone,
  Loader2,
  Plus,
  Settings,
  Trash2,
  X,
  CheckCircle2,
} from 'lucide-react';
import toast from 'react-hot-toast';
import api, { getSocket } from '../services/api';

const fmtTime = (ts) => {
  if (!ts) return '';
  const d = new Date(ts);
  if (isNaN(d)) return '';
  const today = new Date();
  const sameDay = d.toDateString() === today.toDateString();
  return sameDay
    ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
};

const renderQr = (value) => {
  if (!value) return null;
  const src =
    typeof value === 'string' && value.startsWith('data:')
      ? value
      : `https://api.qrserver.com/v1/create-qr-code/?size=260x260&data=${encodeURIComponent(value)}`;
  return <img src={src} alt="WhatsApp QR" className="w-64 h-64 mx-auto" />;
};

const StatusBadge = ({ session }) => {
  const connected = session?.connected;
  const label = connected ? 'Ready' : session?.status || 'disconnected';
  return (
    <span
      className={`px-2 py-0.5 text-xs rounded-full capitalize ${
        connected ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
      }`}
    >
      {label}
    </span>
  );
};

const WhatsAppInbox = () => {
  const [session, setSession] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [search, setSearch] = useState('');
  const [draft, setDraft] = useState('');
  const [loadingConvs, setLoadingConvs] = useState(true);
  const [loadingMsgs, setLoadingMsgs] = useState(false);
  const [sending, setSending] = useState(false);

  // session manager
  const [showSessions, setShowSessions] = useState(false);
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);
  const [qrModal, setQrModal] = useState(null); // { id, name, qr }

  const activeIdRef = useRef(null);
  const threadRef = useRef(null);
  const qrPollRef = useRef(null);
  activeIdRef.current = activeId;

  const isConnected = !!session?.connected;

  // ---- data loading -------------------------------------------------------

  const loadSession = useCallback(async () => {
    try {
      const res = await api.getWaSessionStatus();
      setSession(res.data.data);
    } catch (err) {
      setSession({ status: 'disconnected', connected: false });
    }
  }, []);

  const loadSessions = useCallback(async () => {
    try {
      const res = await api.getWaSessions();
      setSessions(res.data.data || []);
    } catch (err) {
      console.error('[WhatsApp] loadSessions', err);
    }
  }, []);

  const loadConversations = useCallback(async () => {
    setLoadingConvs(true);
    try {
      const res = await api.getWaConversations({ search: search || undefined });
      setConversations(res.data.data || []);
    } catch (err) {
      console.error('[WhatsApp] loadConversations', err);
    } finally {
      setLoadingConvs(false);
    }
  }, [search]);

  const loadMessages = useCallback(async (conversationId) => {
    setLoadingMsgs(true);
    try {
      const res = await api.getWaMessages(conversationId);
      setMessages(res.data.data || []);
      api.markWaRead(conversationId).catch(() => {});
      setConversations((prev) =>
        prev.map((c) => (c.id === conversationId ? { ...c, unread_count: 0 } : c))
      );
    } catch (err) {
      console.error('[WhatsApp] loadMessages', err);
    } finally {
      setLoadingMsgs(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
    loadSessions();
    loadConversations();
  }, [loadSession, loadSessions, loadConversations]);

  useEffect(() => {
    if (activeId) loadMessages(activeId);
  }, [activeId, loadMessages]);

  useEffect(() => {
    if (threadRef.current) threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [messages]);

  useEffect(() => () => clearInterval(qrPollRef.current), []);

  // ---- live updates -------------------------------------------------------

  useEffect(() => {
    const socket = getSocket();

    const onMessage = ({ conversation, message }) => {
      if (!conversation || !message) return;

      if (conversation.id === activeIdRef.current) {
        setMessages((prev) => {
          if (prev.some((m) => m.id === message.id)) return prev;
          return [...prev, message];
        });
        if (message.direction === 'in') api.markWaRead(conversation.id).catch(() => {});
      }

      setConversations((prev) => {
        const idx = prev.findIndex((c) => c.id === conversation.id);
        const bumpUnread =
          message.direction === 'in' && conversation.id !== activeIdRef.current;
        const merged = {
          ...(idx >= 0 ? prev[idx] : {}),
          ...conversation,
          unread_count: bumpUnread
            ? (idx >= 0 ? prev[idx].unread_count || 0 : 0) + 1
            : conversation.id === activeIdRef.current
            ? 0
            : conversation.unread_count,
        };
        const rest = idx >= 0 ? prev.filter((_, i) => i !== idx) : prev;
        return [merged, ...rest];
      });
    };

    socket.on('wa_message', onMessage);
    return () => { socket.off('wa_message', onMessage); };
  }, []);

  // ---- message actions ----------------------------------------------------

  const handleSend = async (e) => {
    e?.preventDefault();
    const text = draft.trim();
    if (!text || !activeId || sending) return;
    setSending(true);
    try {
      const res = await api.sendWaMessage(activeId, text);
      const msg = res.data.data?.message;
      if (msg) setMessages((prev) => (prev.some((m) => m.id === msg.id) ? prev : [...prev, msg]));
      setDraft('');
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to send message');
    } finally {
      setSending(false);
    }
  };

  // ---- session actions ----------------------------------------------------

  const pollQr = (id) => {
    clearInterval(qrPollRef.current);
    const tick = async () => {
      try {
        const res = await api.getWaSessionQr(id);
        const d = res.data.data || {};
        if (d.connected) {
          clearInterval(qrPollRef.current);
          setQrModal(null);
          toast.success('WhatsApp connected');
          await Promise.all([loadSessions(), loadSession(), loadConversations()]);
          return;
        }
        setQrModal((prev) => (prev && prev.id === id ? { ...prev, qr: d.qr } : prev));
      } catch (err) {
        // keep polling; transient errors are expected while the engine spins up
      }
    };
    tick();
    qrPollRef.current = setInterval(tick, 3000);
    // stop after 2 minutes
    setTimeout(() => clearInterval(qrPollRef.current), 120000);
  };

  const openQrFor = (sessionObj) => {
    setQrModal({ id: sessionObj.id, name: sessionObj.name, qr: null });
    pollQr(sessionObj.id);
  };

  const closeQr = () => {
    clearInterval(qrPollRef.current);
    setQrModal(null);
    loadSessions();
    loadSession();
  };

  const handleCreate = async () => {
    const name = newName.trim();
    if (!name) return;
    setCreating(true);
    try {
      const res = await api.createWaSession(name);
      const created = res.data.data;
      setNewName('');
      await loadSessions();
      openQrFor(created);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create session');
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this session? You will need to re-scan to reconnect.')) return;
    try {
      await api.deleteWaSession(id);
      toast.success('Session deleted');
      await Promise.all([loadSessions(), loadSession()]);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete session');
    }
  };

  const activeConv = conversations.find((c) => c.id === activeId);

  // ---- render -------------------------------------------------------------

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <MessageCircle className="w-6 h-6 text-green-600" />
          <h1 className="text-2xl font-bold text-gray-900">WhatsApp Inbox</h1>
          {session && (
            <span className="ml-2 flex items-center gap-2 text-sm text-gray-500">
              {session.name ? `${session.name}` : ''}
              {session.phone ? ` · ${session.phone}` : ''}
              <StatusBadge session={session} />
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => { setShowSessions(true); loadSessions(); }}
            className="inline-flex items-center gap-1.5 px-3 py-2 text-sm text-gray-700 border border-gray-200 rounded-lg hover:bg-gray-50"
          >
            <Settings className="w-4 h-4" /> Sessions
          </button>
          <button
            onClick={() => { loadSession(); loadSessions(); loadConversations(); }}
            className="p-2 text-gray-500 rounded-lg hover:bg-gray-100"
            title="Refresh"
          >
            <RefreshCw className="w-5 h-5" />
          </button>
        </div>
      </div>

      {!isConnected ? (
        <div className="bg-white border border-gray-200 rounded-xl p-10 text-center">
          <Smartphone className="w-12 h-12 mx-auto text-gray-400 mb-4" />
          <h2 className="text-lg font-semibold text-gray-900">No connected WhatsApp session</h2>
          <p className="text-gray-500 mt-1 mb-6 max-w-md mx-auto">
            Create a session and scan the QR with WhatsApp (Linked Devices), or connect
            an existing one — all from here.
          </p>
          <button
            onClick={() => { setShowSessions(true); loadSessions(); }}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700"
          >
            <Settings className="w-4 h-4" /> Manage Sessions
          </button>
        </div>
      ) : (
        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden flex h-[calc(100vh-220px)]">
          {/* Conversation list */}
          <div className="w-80 border-r border-gray-200 flex flex-col">
            <div className="p-3 border-b border-gray-200">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && loadConversations()}
                  placeholder="Search conversations"
                  className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loadingConvs ? (
                <div className="p-6 text-center text-gray-400">
                  <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                </div>
              ) : conversations.length === 0 ? (
                <div className="p-6 text-center text-gray-400 text-sm">No conversations yet</div>
              ) : (
                conversations.map((c) => (
                  <button
                    key={c.id}
                    onClick={() => setActiveId(c.id)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-100 hover:bg-gray-50 ${
                      c.id === activeId ? 'bg-green-50' : ''
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 truncate">
                        {c.contact_name || c.contact_number || c.wa_chat_id}
                      </span>
                      <span className="text-xs text-gray-400 flex-shrink-0 ml-2">
                        {fmtTime(c.last_message_at)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between mt-0.5">
                      <span className="text-sm text-gray-500 truncate">
                        {c.last_message_preview || ''}
                      </span>
                      {c.unread_count > 0 && (
                        <span className="ml-2 flex-shrink-0 bg-green-600 text-white text-xs rounded-full px-2 py-0.5">
                          {c.unread_count}
                        </span>
                      )}
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Thread */}
          <div className="flex-1 flex flex-col">
            {!activeConv ? (
              <div className="flex-1 flex items-center justify-center text-gray-400">
                <div className="text-center">
                  <MessageCircle className="w-10 h-10 mx-auto mb-2" />
                  Select a conversation
                </div>
              </div>
            ) : (
              <>
                <div className="px-4 py-3 border-b border-gray-200">
                  <div className="font-medium text-gray-900">
                    {activeConv.contact_name || activeConv.contact_number || activeConv.wa_chat_id}
                  </div>
                  <div className="text-xs text-gray-400">{activeConv.contact_number}</div>
                </div>

                <div ref={threadRef} className="flex-1 overflow-y-auto p-4 space-y-2 bg-gray-50">
                  {loadingMsgs ? (
                    <div className="text-center text-gray-400">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto" />
                    </div>
                  ) : (
                    messages.map((m) => (
                      <div
                        key={m.id}
                        className={`flex ${m.direction === 'out' ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[70%] px-3 py-2 rounded-lg text-sm ${
                            m.direction === 'out'
                              ? 'bg-green-600 text-white rounded-br-none'
                              : 'bg-white text-gray-800 border border-gray-200 rounded-bl-none'
                          }`}
                        >
                          {m.type !== 'text' && m.media_url && (
                            <a
                              href={m.media_url}
                              target="_blank"
                              rel="noreferrer"
                              className="underline block mb-1"
                            >
                              [{m.type}]
                            </a>
                          )}
                          <span className="whitespace-pre-wrap break-words">{m.body}</span>
                          <div
                            className={`text-[10px] mt-1 ${
                              m.direction === 'out' ? 'text-green-100' : 'text-gray-400'
                            }`}
                          >
                            {fmtTime(m.created_at)}
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <form onSubmit={handleSend} className="p-3 border-t border-gray-200 flex items-center gap-2">
                  <input
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    placeholder="Type a message"
                    className="flex-1 px-4 py-2 border border-gray-200 rounded-full focus:outline-none focus:ring-2 focus:ring-green-500"
                  />
                  <button
                    type="submit"
                    disabled={sending || !draft.trim()}
                    className="p-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 disabled:opacity-50"
                  >
                    {sending ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sessions manager modal */}
      {showSessions && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">WhatsApp Sessions</h3>
              <button onClick={() => setShowSessions(false)} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto p-5 space-y-2">
              {sessions.length === 0 ? (
                <p className="text-sm text-gray-400 text-center py-4">No sessions yet. Create one below.</p>
              ) : (
                sessions.map((s) => (
                  <div key={s.id} className="flex items-center justify-between border border-gray-200 rounded-lg px-3 py-2.5">
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-900 truncate">{s.name || s.id}</span>
                        <StatusBadge session={s} />
                      </div>
                      <div className="text-xs text-gray-400">{s.phone || s.id}</div>
                    </div>
                    <div className="flex items-center gap-1.5 flex-shrink-0">
                      {!s.connected && (
                        <button
                          onClick={() => openQrFor(s)}
                          className="px-2.5 py-1.5 text-xs text-green-700 bg-green-50 rounded-md hover:bg-green-100"
                        >
                          Connect
                        </button>
                      )}
                      <button
                        onClick={() => handleDelete(s.id)}
                        className="p-1.5 text-gray-400 hover:text-red-600"
                        title="Delete session"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>

            <div className="px-5 py-4 border-t flex items-center gap-2">
              <input
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                placeholder="New session name (e.g. support)"
                className="flex-1 px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
              />
              <button
                onClick={handleCreate}
                disabled={creating || !newName.trim()}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                Create
              </button>
            </div>
          </div>
        </div>
      )}

      {/* QR modal */}
      {qrModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-6 text-center">
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold text-gray-900">
                Scan to connect {qrModal.name ? `“${qrModal.name}”` : ''}
              </h3>
              <button onClick={closeQr} className="p-1 text-gray-400 hover:text-gray-600">
                <X className="w-5 h-5" />
              </button>
            </div>
            <p className="text-sm text-gray-500 mb-4">
              WhatsApp → Linked Devices → Link a Device
            </p>
            {qrModal.qr ? (
              renderQr(qrModal.qr)
            ) : (
              <div className="py-16 text-gray-400">
                <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2" />
                Generating QR…
              </div>
            )}
            <p className="text-xs text-gray-400 mt-4 flex items-center justify-center gap-1">
              <CheckCircle2 className="w-3.5 h-3.5" /> This closes automatically once linked
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default WhatsAppInbox;
